// Axios instance and types
import http from '../api'
import { TypeUserAccess, TypeUserAccessUpdate, TypeUserInvitation, TypeUserLogin } from '../../types/User'
import router from '../../router'
import { APIResponse, Filters, APIPaginatedResponse, Pagination } from '../types'
import { getRequestParamString } from '../utils/utils'
import { getSessionManager } from '../../utils/sessionManager'
import { UserPreference } from '../../types/User'

const apiPath = import.meta.env.VITE_API_PATH

async function getUsers(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
  return await http.get<APIPaginatedResponse<any>>(apiPath + 'users' + getRequestParamString(filters, pagination))
}

async function userLogin(input: TypeUserLogin) {
  // Get csrf-cookie
  await http.get('/sanctum/csrf-cookie')
  return await http.post<string>(`${apiPath}auth/login`, input)
}

async function userLogOut() {
  return await http.post<string>(`${apiPath}auth/logout`).then(async () => {
    // End the session to allow other tabs to log in
    const sessionManager = getSessionManager()
    sessionManager.endSession()
    // Clear SPA auth so a subsequent login cannot reuse a stale hydrated user.
    const { useUserStore } = await import('../../store/user')
    useUserStore().clearAuthState()
    router.replace({ name: 'login' })
  })
}

async function getAuthenticatedUser() {
  return await http.get<any>(`${apiPath}auth/user`, { timeout: 15000 })
}

async function setUserPreferences(payload: UserPreference) {
  return await http.post<APIResponse<any>>(`${apiPath}auth/user/preferences/set`, payload)
}

async function getUserPermissions() {
  return await http.get<APIResponse<any>>(`${apiPath}auth/permissions`)
}

async function getUserInvitationOfUuid(uuid: string) {
  return await http.get<any>(`${apiPath}users/invitation/${uuid}`)
}

async function sendUserInvitation(userData: Partial<TypeUserInvitation>) {
  const payLoad: Record<string, unknown> = {
    name: userData.name,
    email: userData.email,
    role_id: userData.role_id,
    phone: userData.phone,
  }

  if (userData.branch_uuid) {
    payLoad.branch_uuid = userData.branch_uuid
  }
  if (userData.location_id != null) {
    payLoad.location_id = userData.location_id
  }

  return await http.post<APIResponse<any>>(`${apiPath}users/invitation`, payLoad)
}

async function getUserInvitations(filters?: Partial<Filters>, pagination?: Partial<Pagination>) {
  return await http.get<APIPaginatedResponse<any>>(
    apiPath + 'users/invitation' + getRequestParamString(filters, pagination),
  )
}

async function deleteUserInvitation(invitationUuid: string) {
  return await http.delete<APIResponse<any>>(`${apiPath}users/invitation/${invitationUuid}`)
}

async function activateUserInvitation(invitationUuid: string, password: string) {
  const payLoad = {
    password: password,
  }

  return await http.post<APIResponse<any>>(`${apiPath}users/invitation/${invitationUuid}/activate`, payLoad)
}

async function updateUserAccess(userUuid: string, payload: TypeUserAccessUpdate) {
  return await http.put<APIResponse<TypeUserAccess>>(`${apiPath}users/${userUuid}/access`, payload)
}

async function sendResetPasswordLink(email: string, requestSource: number) {
  const payLoad = {
    email: email,
    source: requestSource,
  }

  return await http.post<APIResponse<any>>(`${apiPath}auth/send-reset-password-link`, payLoad)
}

async function resetPassword(token: string, email: string, password: string, confirmPassword: string) {
  const payLoad = {
    token: token,
    email: email,
    password: password,
    password_confirmation: confirmPassword,
  }

  return await http.post<APIResponse<any>>(`${apiPath}auth/reset-password`, payLoad)
}

export default {
  userLogin,
  userLogOut,
  getAuthenticatedUser,
  setUserPreferences,
  getUserPermissions,
  getUsers,
  sendUserInvitation,
  getUserInvitations,
  deleteUserInvitation,
  activateUserInvitation,
  getUserInvitationOfUuid,
  updateUserAccess,
  sendResetPasswordLink,
  resetPassword,
}
